import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Icone } from './Icone'

/**
 * Modale, feuille et menu — tout ce qui se pose par-dessus l'écran.
 *
 * Un même composant sert la modale et la feuille : sous 40 rem elle monte du bas et occupe la
 * largeur entière, au-delà elle est centrée. C'est une décision de feuille de style, pas deux
 * composants — deux composants divergeraient.
 *
 * Trois comportements sont attendus de tout ce qui se superpose, et aucun n'est automatique :
 * Échap ferme, le clic sur le voile ferme, et le focus revient d'où il venait.
 */

export function Modale({
  titre,
  children,
  pied,
  feuille = false,
  large = false,
  onFermer,
}: {
  titre: string
  children: ReactNode
  pied?: ReactNode
  /** Plein écran par le bas en compact. Pour les filtres et les formulaires courts. */
  feuille?: boolean
  /**
   * Presque toute la fenêtre. Pour ce dont la TAILLE est le contenu — une photo qu on ouvre
   * pour la voir en grand (ticket `fo-annonce-04`). Une visionneuse dans une modale de la
   * largeur d un formulaire ne montre pas plus que la vignette qu on vient de quitter.
   */
  large?: boolean
  onFermer: () => void
}) {
  const boite = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const precedent = document.activeElement as HTMLElement | null
    boite.current?.focus()

    function surTouche(evenement: KeyboardEvent) {
      if (evenement.key === 'Escape') {
        onFermer()
      }
    }

    document.addEventListener('keydown', surTouche)

    /*
      Le défilement de la page est verrouillé pendant que la modale est ouverte.

      Sans cela, la molette traverse la surcouche et fait défiler la liste de résultats
      DERRIÈRE le voile : on revient sur un écran qu'on ne reconnaît plus, sans avoir rien
      demandé. C'est le ticket `fo-recherche-04`.

      La barre de défilement est compensée par un remplissage de la même largeur : la retirer
      sans compenser décale toute la page de quinze pixels au moment de l'ouverture, ce qui
      produit un sursaut plus visible que le défaut qu'on corrige. `padding-inline-end` et non
      `padding-right` — la barre passe à gauche en RTL (invariant 11).
    */
    const corps = document.body
    const debordementInitial = corps.style.overflow
    const remplissageInitial = corps.style.paddingInlineEnd
    const largeurBarre = window.innerWidth - document.documentElement.clientWidth

    corps.style.overflow = 'hidden'

    if (largeurBarre > 0) {
      corps.style.paddingInlineEnd = `${largeurBarre}px`
    }

    return () => {
      document.removeEventListener('keydown', surTouche)
      corps.style.overflow = debordementInitial
      corps.style.paddingInlineEnd = remplissageInitial
      // Le focus revient sur ce qui a ouvert la modale. Sans cela, il repart au début du
      // document : au clavier, on recommence la page entière après chaque fermeture.
      precedent?.focus()
    }
  }, [onFermer])

  return (
    <div
      className={feuille ? 'voile voile--feuille' : 'voile'}
      onClick={(evenement) => {
        if (evenement.target === evenement.currentTarget) {
          onFermer()
        }
      }}
    >
      <div
        className={large ? 'modale modale--large' : 'modale'}
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        tabIndex={-1}
        ref={boite}
      >
        <div className="modale__entete">
          <h2>{titre}</h2>
          <button type="button" className="entete__icone" onClick={onFermer} aria-label="Fermer">
            <Icone nom="croix" />
          </button>
        </div>
        <div className="modale__corps">{children}</div>
        {pied !== undefined && <div className="modale__pied">{pied}</div>}
      </div>
    </div>
  )
}

/**
 * Menu déroulant.
 *
 * Il se ferme au clic à l'extérieur et à Échap — deux gestes qu'un utilisateur fait sans y penser
 * et dont l'absence se remarque immédiatement.
 */
export function Menu({
  declencheur,
  children,
  ariaLabel,
  classeDeclencheur = 'compte-bouton',
  aligne = 'fin',
}: {
  declencheur: (ouvert: boolean) => ReactNode
  children: ReactNode
  ariaLabel: string
  /** Le menu du compte porte un avatar ; celui de la langue est une icône. Deux allures, un seul composant. */
  classeDeclencheur?: string
  /** Le bord sur lequel le panneau s'aligne. Logique, donc retourné en RTL sans y penser. */
  aligne?: 'debut' | 'fin'
}) {
  const [ouvert, setOuvert] = useState(false)
  const racine = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ouvert) {
      return
    }

    function surClic(evenement: MouseEvent) {
      if (racine.current !== null && !racine.current.contains(evenement.target as Node)) {
        setOuvert(false)
      }
    }

    function surTouche(evenement: KeyboardEvent) {
      if (evenement.key === 'Escape') {
        setOuvert(false)
      }
    }

    document.addEventListener('mousedown', surClic)
    document.addEventListener('keydown', surTouche)

    return () => {
      document.removeEventListener('mousedown', surClic)
      document.removeEventListener('keydown', surTouche)
    }
  }, [ouvert])

  return (
    <div className="menu" ref={racine}>
      <button
        type="button"
        className={classeDeclencheur}
        aria-expanded={ouvert}
        aria-haspopup="true"
        aria-label={ariaLabel}
        onClick={() => setOuvert(!ouvert)}
      >
        {declencheur(ouvert)}
      </button>

      <div
        className={aligne === 'debut' ? 'menu__panneau menu__panneau--debut' : 'menu__panneau'}
        hidden={!ouvert}
        onClick={() => setOuvert(false)}
      >
        {children}
      </div>
    </div>
  )
}
